/**
 * TaskDetailsMetadata Component
 *
 * Renders the task metadata details card including:
 * - Urgency
 * - Start Date, Due Date, Created On
 * - Requested By, Channel Partner, Advertiser
 * - Request Type, Request Details
 * - External link
 *
 * Supports inline editing (pencil icon) for editable fields.
 * Requested By (createdBy) and Created On remain read-only.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Row, Col, Tooltip, Tag, Select, DatePicker } from 'antd';
import { EditOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import {
  urgencyColors,
  urgencyTypes,
  taskTypes,
  subTypes,
} from '../../configs/task.config';
import { neutral, bg } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Option } = Select;

const TaskDetailsMetadata = ({
  currentTask,
  level,
  isEditingDetails,
  setIsEditingDetails,
  detailsForm,
  onDetailsFieldChange,
  onDetailsSave,
  onDetailsCancel,
  updateTaskLoading,
  channelPartnerOrgs,
  advertiserOrgs,
  basicOrgsDetailsLoading,
  fetchChannelPartnerOrgs,
  fetchAdvertiserOrgs,
}) => {
  const renderDetailItem = (label, value) => (
    <Col className="task-metadata__field">
      <Text strong className="task-metadata__label">
        {label}
      </Text>
      <Text className="task-metadata__value">
        {value || '-'}
      </Text>
    </Col>
  );

  const availableSubTypes = detailsForm.requestType
    ? subTypes[detailsForm.requestType] || []
    : [];

  // Mirrors the rules in TaskBasicFields: due date required only when not linked to
  // a project; start must be strictly before due when both are set.
  const isDueDateRequired = !(currentTask && currentTask.project);
  const isDueDateMissing = isDueDateRequired && !detailsForm.dueDate;
  const isStartAfterOrEqualDue =
    !!detailsForm.startDate &&
    !!detailsForm.dueDate &&
    !detailsForm.startDate.isBefore(detailsForm.dueDate, 'day');
  const isSaveDisabled =
    updateTaskLoading ||
    !detailsForm.channelPartnerId ||
    !detailsForm.advertiserId ||
    isDueDateMissing ||
    isStartAfterOrEqualDue;

  const onEditClick = () => {
    setIsEditingDetails(true);
    fetchChannelPartnerOrgs();
    if (currentTask && currentTask.channelPartner && currentTask.channelPartner.id) {
      fetchAdvertiserOrgs('', currentTask.channelPartner.id);
    }
  };

  return (
    <Card className="task-metadata__card">
      <div className="task-metadata__container">
        <Row className="task-metadata__header">
          <Text strong className="task-metadata__title">
            Details
          </Text>
          <div className="task-metadata__header-actions">
            {currentTask && currentTask.linkUrl && (
              <Tooltip title={currentTask.linkUrl}>
                <LinkOutlined
                  className="task-metadata__link-icon"
                  onClick={() => {
                    const url = currentTask?.linkUrl;
                    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                />
              </Tooltip>
            )}
            {!isEditingDetails && level === 'PLATFORM' && (
              <EditOutlined
                className="task-metadata__edit-icon"
                onClick={onEditClick}
              />
            )}
          </div>
        </Row>

        {/* Urgency */}
        <Col className="task-metadata__field task-metadata__field--narrow">
          <Text strong className="task-metadata__label">
            Urgency
          </Text>
          {isEditingDetails ? (
            <Select
              className="custom-select"
              style={{ width: '100%' }}
              value={detailsForm.urgency || undefined}
              placeholder="Select Urgency"
              allowClear
              onChange={(value) => onDetailsFieldChange('urgency', value || null)}
            >
              {Object.entries(urgencyTypes).map(([label, value]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          ) : currentTask && currentTask.urgency ? (
            <Tag
              className="task-metadata__urgency-tag"
              style={{
                backgroundColor:
                  currentTask && currentTask.urgency
                    ? urgencyColors[currentTask.urgency]
                    : 'gray',
              }}
            >
              <Row justify="center" type="flex" align="middle" className="task-metadata__urgency-content">
                <div
                  className="task-metadata__urgency-dot"
                  style={{
                    backgroundColor:
                      currentTask && currentTask.urgency === 'MEDIUM' ? neutral[900] : bg.card,
                  }}
                />
                <Text
                  strong
                  className="task-metadata__urgency-text"
                  style={{
                    color:
                      currentTask && currentTask.urgency === 'MEDIUM' ? neutral[900] : bg.card,
                  }}
                >
                  {currentTask && currentTask.urgency
                    ? startCase(currentTask.urgency.toLowerCase())
                    : '-'}
                </Text>
              </Row>
            </Tag>
          ) : (
            <Text className="task-metadata__value">{`-`}</Text>
          )}
        </Col>

        {/* Start Date */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Start Date
          </Text>
          {isEditingDetails ? (
            <DatePicker
              style={{ width: '100%' }}
              value={detailsForm.startDate}
              onChange={(value) => onDetailsFieldChange('startDate', value)}
            />
          ) : (
            <Text className="task-metadata__value">
              {currentTask && currentTask.startDate
                ? new Date(currentTask.startDate).toLocaleDateString()
                : '-'}
            </Text>
          )}
        </Col>

        {/* Due Date (required when not linked to a project) */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Due Date
            {isEditingDetails && isDueDateRequired && (
              <span style={{ color: '#ff4d4f' }}> *</span>
            )}
          </Text>
          {isEditingDetails ? (
            <DatePicker
              style={{ width: '100%' }}
              value={detailsForm.dueDate}
              onChange={(value) => onDetailsFieldChange('dueDate', value)}
            />
          ) : (
            <Text className="task-metadata__value">
              {currentTask && currentTask.dueDate
                ? new Date(currentTask.dueDate).toLocaleDateString()
                : '-'}
            </Text>
          )}
        </Col>

        {renderDetailItem(
          'Created On',
          currentTask && currentTask.createdAt
            ? new Date(currentTask.createdAt).toLocaleDateString()
            : 'N/A'
        )}

        {/* Requested By (read-only; editing would rewrite task authorship) */}
        {renderDetailItem(
          'Requested By',
          startCase(
            currentTask &&
              currentTask.createdBy &&
              (currentTask.createdBy.name || currentTask.createdBy.username || 'Unknown')
          )
        )}

        {/* Channel Partner (required) */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Channel Partner
            {isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
          </Text>
          {isEditingDetails ? (
            <Select
              className="custom-select"
              style={{ width: '100%' }}
              value={detailsForm.channelPartnerId || undefined}
              placeholder="Select Channel Partner"
              showSearch
              loading={basicOrgsDetailsLoading}
              onFocus={() => fetchChannelPartnerOrgs()}
              onSearch={(value) => fetchChannelPartnerOrgs(value)}
              onChange={(value) => {
                onDetailsFieldChange('channelPartnerId', value || null);
                if (value) fetchAdvertiserOrgs('', value);
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {channelPartnerOrgs.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          ) : (
            <Text className="task-metadata__value">
              {currentTask && currentTask.channelPartner
                ? currentTask.channelPartner.name
                : '-'}
            </Text>
          )}
        </Col>

        {/* Advertiser (required) */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Advertiser
            {isEditingDetails && <span style={{ color: '#ff4d4f' }}> *</span>}
          </Text>
          {isEditingDetails ? (
            <Select
              className="custom-select"
              style={{ width: '100%' }}
              value={detailsForm.advertiserId || undefined}
              placeholder={
                detailsForm.channelPartnerId
                  ? 'Select Advertiser'
                  : 'Select a channel partner first'
              }
              showSearch
              disabled={!detailsForm.channelPartnerId}
              loading={basicOrgsDetailsLoading}
              onFocus={() => fetchAdvertiserOrgs()}
              onSearch={(value) => fetchAdvertiserOrgs(value)}
              onChange={(value) => onDetailsFieldChange('advertiserId', value || null)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {advertiserOrgs.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          ) : (
            <Text className="task-metadata__value">
              {currentTask && currentTask.advertiser
                ? currentTask.advertiser.name
                : '-'}
            </Text>
          )}
        </Col>

        {/* Request Type */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Request Type
          </Text>
          {isEditingDetails ? (
            <Select
              className="custom-select"
              style={{ width: '100%' }}
              value={detailsForm.requestType || undefined}
              placeholder="Select Request Type"
              allowClear
              showSearch
              onChange={(value) => onDetailsFieldChange('requestType', value || null)}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {taskTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          ) : (
            <Text className="task-metadata__value">
              {(currentTask && currentTask.requestType) || '-'}
            </Text>
          )}
        </Col>

        {/* Request Details (sub-type) */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Request Details
          </Text>
          {isEditingDetails ? (
            <Select
              className="custom-select"
              style={{ width: '100%' }}
              value={detailsForm.requestSubType || undefined}
              placeholder={
                availableSubTypes.length > 0
                  ? 'Select Sub Type'
                  : 'No sub-types for this request type'
              }
              allowClear
              disabled={availableSubTypes.length === 0}
              onChange={(value) => onDetailsFieldChange('requestSubType', value || null)}
            >
              {availableSubTypes.map((sub) => (
                <Option key={sub} value={sub}>
                  {sub}
                </Option>
              ))}
            </Select>
          ) : (
            <Text className="task-metadata__value">
              {(currentTask && currentTask.requestSubType) || '-'}
            </Text>
          )}
        </Col>

        {isEditingDetails && isStartAfterOrEqualDue && (
          <Text type="danger" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Start date must be before due date.
          </Text>
        )}

        {isEditingDetails && (
          <div className="task-metadata__edit-actions">
            <Button
              type="primary"
              onClick={onDetailsSave}
              disabled={isSaveDisabled}
              className="task-metadata__save-btn"
            >
              {updateTaskLoading && <LoadingOutlined />} Save
            </Button>
            <Button
              onClick={onDetailsCancel}
              disabled={updateTaskLoading}
              className="task-metadata__cancel-btn"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

TaskDetailsMetadata.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    urgency: PropTypes.oneOf(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']),
    startDate: PropTypes.string,
    dueDate: PropTypes.string,
    createdAt: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      username: PropTypes.string,
    }),
    channelPartner: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    advertiser: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    linkUrl: PropTypes.string,
    project: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  level: PropTypes.string,
  isEditingDetails: PropTypes.bool,
  setIsEditingDetails: PropTypes.func,
  detailsForm: PropTypes.shape({
    urgency: PropTypes.string,
    startDate: PropTypes.object,
    dueDate: PropTypes.object,
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    channelPartnerId: PropTypes.string,
    advertiserId: PropTypes.string,
  }),
  onDetailsFieldChange: PropTypes.func,
  onDetailsSave: PropTypes.func,
  onDetailsCancel: PropTypes.func,
  updateTaskLoading: PropTypes.bool,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  fetchChannelPartnerOrgs: PropTypes.func,
  fetchAdvertiserOrgs: PropTypes.func,
};

TaskDetailsMetadata.defaultProps = {
  currentTask: null,
  level: null,
  isEditingDetails: false,
  setIsEditingDetails: () => {},
  detailsForm: {
    urgency: null,
    startDate: null,
    dueDate: null,
    requestType: null,
    requestSubType: null,
    channelPartnerId: null,
    advertiserId: null,
  },
  onDetailsFieldChange: () => {},
  onDetailsSave: () => {},
  onDetailsCancel: () => {},
  updateTaskLoading: false,
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  basicOrgsDetailsLoading: false,
  fetchChannelPartnerOrgs: () => {},
  fetchAdvertiserOrgs: () => {},
};

export default TaskDetailsMetadata;
